import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  TrendingUp,
  TrendingDown,
  Minus,
  Pencil,
  Trash2,
  BarChart3,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { DataTrackingForm } from "./DataTrackingForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackingEntry {
  id: number;
  person_id: number;
  agency_id: string;
  category: string;
  label: string;
  value: number | null;
  text_value: string | null;
  measurement_date: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string | null;
  author_first_name?: string;
  author_last_name?: string;
}

interface DataTrackingListProps {
  personId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataTrackingList({ personId }: DataTrackingListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TrackingEntry | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const { data: trackingData, isLoading } = useQuery<{
    data: TrackingEntry[];
  }>({
    queryKey: ["tracking", personId],
    queryFn: () =>
      api.get<{ data: TrackingEntry[] }>(`/clients/${personId}/tracking`),
    enabled: !!personId,
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: number) =>
      api.delete(`/clients/${personId}/tracking/${entryId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking", personId] });
    },
  });

  const entries = trackingData?.data ?? [];

  // Get unique categories
  const categories = Array.from(new Set(entries.map((e) => e.category)));

  const filteredEntries =
    categoryFilter === "all"
      ? entries
      : entries.filter((e) => e.category === categoryFilter);

  // Group by category for trend calculation
  function getTrend(entry: TrackingEntry): "up" | "down" | "flat" | null {
    if (entry.value === null) return null;
    const sameCategory = entries
      .filter(
        (e) =>
          e.category === entry.category &&
          e.label === entry.label &&
          e.value !== null,
      )
      .sort(
        (a, b) =>
          new Date(b.measurement_date).getTime() -
          new Date(a.measurement_date).getTime(),
      );

    const idx = sameCategory.findIndex((e) => e.id === entry.id);
    if (idx < 0 || idx >= sameCategory.length - 1) return null;

    const prev = sameCategory[idx + 1]?.value ?? 0;
    const current = entry.value ?? 0;
    if (current > prev) return "up";
    if (current < prev) return "down";
    return "flat";
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Data Tracking</h2>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            setEditingEntry(null);
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Entry
        </Button>
      </div>

      {/* Form */}
      {(showForm || editingEntry) && (
        <DataTrackingForm
          personId={personId}
          entry={editingEntry ?? undefined}
          onClose={() => {
            setShowForm(false);
            setEditingEntry(null);
          }}
        />
      )}

      {/* Category filter */}
      {categories.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Category:</span>
          <button
            onClick={() => setCategoryFilter("all")}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium transition-colors",
              categoryFilter === "all"
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80",
            )}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                categoryFilter === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80",
              )}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && entries.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No tracking data recorded yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Entries list */}
      {!isLoading && filteredEntries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Label
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Value
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Trend
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Notes
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry) => {
                  const trend = getTrend(entry);
                  return (
                    <tr key={entry.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        {new Date(
                          entry.measurement_date,
                        ).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                          {entry.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{entry.label}</td>
                      <td className="px-4 py-3">
                        {entry.value !== null ? (
                          <span className="font-mono">{entry.value}</span>
                        ) : entry.text_value ? (
                          <span>{entry.text_value}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {trend === "up" && (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        )}
                        {trend === "down" && (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                        {trend === "flat" && (
                          <Minus className="h-4 w-4 text-gray-400" />
                        )}
                        {trend === null && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        {entry.notes ? (
                          <span
                            className="text-muted-foreground line-clamp-1"
                            title={entry.notes}
                          >
                            {entry.notes}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingEntry(entry);
                              setShowForm(false);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Delete this tracking entry?",
                                )
                              ) {
                                deleteMutation.mutate(entry.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
